import * as _ from 'lodash-es';
import React, { useMemo } from 'react';
import useCurrentPostTopicIds from '../post/useCurrentPostTopicIds';
import { filterTreeListTopics } from '../topic/toTopicTreeList';
import type { AllTopicsTreeListTopic } from '../topic/useAllTopics';
import useAllTopics from '../topic/useAllTopics';
import { isFeatureEnabled } from '../utils/features';
import Favorites from './Favorites';
import SidebarExternalPostsSection from './SidebarExternalPostsSection';
import SidebarSections from './SidebarSections';
import { useSidebarSectionsContext } from './SidebarSectionsProvider';
import SidebarTopics from './SidebarTopics';
import isNotDetached from './isNotDetached';

type TopicStates = {
  active?: Record<string, boolean>;
  expanded?: Record<string, boolean>;
};

const getTopicStates = (
  activeId: string,
  topics: AllTopicsTreeListTopic[],
): TopicStates | null =>
  topics.reduce((result: TopicStates | null, { id, childTopics }) => {
    if (result) return result;

    if (activeId === id) {
      return { active: { [id]: true } };
    }

    const childResult = getTopicStates(activeId, childTopics);

    if (childResult) {
      return {
        active: childResult.active,
        expanded: {
          [id]: true,
          ...childResult.expanded,
        },
      };
    }

    return null;
  }, null);

export const getAllTopicStates = (
  ids: string[],
  topics: AllTopicsTreeListTopic[],
) => {
  const states = ids.map((id) => getTopicStates(id, topics));
  return _.merge({ expanded: {}, active: {} }, ...states);
};

interface LegacySidebarSectionsProps {
  topicId?: string;
}

const LegacySidebarSections = ({ topicId }: LegacySidebarSectionsProps) => {
  const postTopicIds = useCurrentPostTopicIds();

  const treeTopics = useAllTopics();

  const { myTopics } = useSidebarSectionsContext();

  const activeTopicIds = topicId ? [topicId] : postTopicIds;
  const { expanded, active } = getAllTopicStates(activeTopicIds, treeTopics);

  const filteredAllTopics = useMemo(
    () => filterTreeListTopics(treeTopics, isNotDetached),
    [treeTopics],
  );

  return (
    <>
      {isFeatureEnabled('sidebar_section') ? (
        <SidebarSections
          activeTopics={active}
          expandedTopics={expanded}
          allTopics={filteredAllTopics}
        />
      ) : (
        <>
          <Favorites activeTopics={active} expandedTopics={expanded} />
          <SidebarTopics
            activeTopics={active}
            expandedTopics={expanded}
            myTopics={myTopics}
            allTopics={filteredAllTopics}
          />
          <SidebarExternalPostsSection />
        </>
      )}
    </>
  );
};

export default LegacySidebarSections;
